import React, { useEffect, useRef, useState } from 'react';
import { X, Eye, Pencil, Upload, Loader2 } from 'lucide-react';
import TaskDescriptionMarkdown from '../Tarefas/TaskDescriptionMarkdown';
import { validarDocumento, tituloSugeridoPara } from '../../src/lib/projetos/documentos';
import type { ProjetoDocumento } from '../../types';

interface Props {
  /**
   * Documento a abrir no editor. `id` ausente/zero = ainda não existe no banco — é o
   * rascunho que vem do import de um .md, que já chega com título e conteúdo mas é uma
   * criação, não uma edição.
   */
  documento?: ProjetoDocumento | null;
  onFechar: () => void;
  onSalvar: (dados: { titulo: string; conteudo: string }) => Promise<void>;
}

/**
 * Editor do documento em markdown, com alternância escrever/visualizar.
 *
 * Duas escolhas que valem explicação:
 *
 * 1. **Alternar, não lado a lado.** O caso de uso dominante é COLAR um .md pronto (foi o
 *    que motivou o módulo) e conferir se ficou legível — não escrever prosa acompanhando o
 *    preview em tempo real. Lado a lado espremeria as duas metades no notebook.
 * 2. **Importar arquivo .md preenche o textarea**, em vez de virar anexo. Um .md guardado
 *    como arquivo volta a ser algo para baixar, que é exatamente o que este módulo veio
 *    resolver — o texto tem que ficar legível dentro do VigorHub.
 */
const DocumentoEditorModal: React.FC<Props> = ({ documento, onFechar, onSalvar }) => {
  const [titulo, setTitulo] = useState(documento?.titulo || '');
  const [conteudo, setConteudo] = useState(documento?.conteudo || '');
  const [visualizando, setVisualizando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);
  const editando = Boolean(documento?.id);

  // Esc fecha: o modal é grande e a pessoa costuma abrir sem querer a partir da lista.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape' && !salvando) onFechar(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [onFechar, salvando]);

  const importarMd = async (arquivo: File) => {
    const texto = await arquivo.text();
    setConteudo(texto);
    if (!titulo.trim()) setTitulo(tituloSugeridoPara(arquivo.name));
    setErro(null);
  };

  const salvar = async () => {
    const validacao = validarDocumento(titulo, conteudo);
    if (!validacao.ok) { setErro(validacao.erro!); return; }
    setSalvando(true);
    setErro(null);
    try {
      await onSalvar({ titulo: titulo.trim(), conteudo });
      onFechar();
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível salvar o documento.');
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true"
      aria-label={editando ? 'Editar documento' : 'Novo documento'}>
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-800 dark:text-white">
            {editando ? 'Editar documento' : 'Novo documento'}
          </h2>
          <button onClick={onFechar} disabled={salvando} aria-label="Fechar"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          <div>
            <label htmlFor="doc-titulo" className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
              Título
            </label>
            <input id="doc-titulo" value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={120}
              placeholder="Ex.: Como funciona o Boleto Único"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1">
              <button onClick={() => setVisualizando(false)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  !visualizando ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}>
                <Pencil size={13} /> Escrever
              </button>
              <button onClick={() => setVisualizando(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  visualizando ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}>
                <Eye size={13} /> Visualizar
              </button>
            </div>
            <button onClick={() => inputArquivo.current?.click()}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary">
              <Upload size={13} /> Importar .md
            </button>
            <input ref={inputArquivo} type="file" accept=".md,.txt" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) importarMd(f); e.target.value = ''; }} />
          </div>

          {visualizando ? (
            <div className="min-h-[300px] px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              {conteudo.trim()
                ? <TaskDescriptionMarkdown value={conteudo} variant="full" />
                : <p className="text-sm text-gray-400">Nada escrito ainda.</p>}
            </div>
          ) : (
            <textarea value={conteudo} onChange={e => setConteudo(e.target.value)} rows={16}
              placeholder={'Cole aqui o conteúdo, ou escreva em markdown.\n\n# Título\n- item\n**negrito**'}
              className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40" />
          )}

          {erro && <p className="text-xs text-rose-600 dark:text-rose-400" role="alert">{erro}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onFechar} disabled={salvando}
            className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={salvar} disabled={salvando}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50">
            {salvando && <Loader2 size={14} className="animate-spin" />}
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentoEditorModal;
